// src/components/forms/variants/v2/hero/HeroEmailCapture.tsx

'use client'

import { forwardRef, useMemo } from 'react'
import { cn } from '~/lib/utils'
import { BaseEmailCaptureForm } from '~/components/forms/base/BaseEmailCaptureForm'
import { FormErrorBoundary } from '~/components/forms/base/ErrorBoundary'
import type { RenderProps } from '~/components/forms/base/types'
import type { HeroEmailCaptureProps } from '~/components/forms/variants/hero/types'
import { HERO_PRESET } from '~/components/forms/variants/hero/types'

export const HeroEmailCapture = forwardRef<HTMLFormElement, HeroEmailCaptureProps>(({
  theme: customTheme,
  content: customContent,
  ...props
}, ref) => {
  // Memoize merged configurations
  const theme = useMemo(() => ({
    ...HERO_PRESET.theme,
    ...customTheme,
    className: {
      ...HERO_PRESET.theme?.className,
      ...customTheme?.className
    }
  }), [customTheme])

  const content = useMemo(() => ({
    ...HERO_PRESET.content,
    ...customContent,
    button: {
      ...HERO_PRESET.content?.button,
      ...customContent?.button
    },
    input: {
      ...HERO_PRESET.content?.input,
      ...customContent?.input
    }
  }), [customContent])

  // Memoize the form render function
  const renderForm = ({ state, handlers }: RenderProps): JSX.Element => {
    const { email, error, isSubmitting, isSuccess } = state
    const { handleChange, handleSubmit, handleBlur } = handlers

    return (
      <div className={cn("max-w-4xl", theme.className?.container)}>
        <div className="flex flex-col items-center justify-center text-center">
          {content.title && (
            <h3 className={cn(
              "inline-block bg-gradient-to-t from-gray-900 to-gray-800 bg-clip-text p-2",
              "text-4xl font-bold tracking-tighter text-transparent md:text-6xl",
              "dark:from-gray-50 dark:to-gray-300",
              content.title.className
            )}>
              {content.title.text}
            </h3>
          )}
          {content.description && (
            <p className={cn(
              "mx-auto mt-4 max-w-2xl text-gray-600 sm:text-lg dark:text-gray-400",
              content.description.className
            )}>
              {content.description.text}
            </p>
          )}
        </div>

        <div className="mt-14 w-full rounded-[16px] bg-gray-300/5 p-1.5 ring-1 ring-black/[3%] 
                      backdrop-blur dark:bg-gray-900/10 dark:ring-white/[3%]">
          <div className="rounded-xl bg-white p-4 shadow-lg shadow-indigo-500/10 ring-1 ring-black/5 
                       dark:bg-gray-950 dark:shadow-indigo-500/10 dark:ring-white/5">
            <div className="relative">
              <div className="relative flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={content.input?.placeholder ?? "Enter your email"}
                  className={cn(
                    'block w-full rounded-lg border-0',
                    'h-10 px-2.5 py-1.5',
                    'text-gray-900 dark:text-white',
                    'shadow-sm ring-1 ring-inset',
                    'ring-gray-200 dark:ring-gray-700',
                    'placeholder:text-gray-500 dark:placeholder:text-gray-400',
                    'focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:focus:ring-indigo-400',
                    'hover:ring-gray-300 dark:hover:ring-gray-600',
                    'transition-shadow duration-200',
                    error && 'ring-red-300 dark:ring-red-500/30 focus:ring-red-500',
                    content.input?.className
                  )}
                  disabled={isSubmitting || isSuccess}
                />
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting || isSuccess}
                  className={cn(
                    'inline-flex h-10 w-full sm:w-fit sm:flex-none items-center justify-center rounded-lg px-3 py-1.5',
                    'border border-indigo-700 bg-indigo-800 text-white',
                    'text-sm font-medium shadow-sm',
                    'transition-all duration-100 ease-in-out',
                    'hover:bg-gradient-to-r hover:from-rose-800 hover:to-indigo-800',
                    'dark:border-indigo-800 dark:bg-indigo-600',
                    'dark:outline-indigo-500',
                    'disabled:opacity-50 dark:disabled:opacity-40',
                    content.button?.className
                  )}
                >
                  <span>{isSubmitting ? content.button?.loadingText : content.button?.text}</span>
                  {content.button?.icon}
                </button>
              </div>

              {/* Messages */}
              {(error ?? isSuccess) && (
                <div className="mt-2">
                  {error && (
                    <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                      {error}
                    </p>
                  )}
                  {isSuccess && (
                    <p className="text-sm text-green-600 dark:text-green-400" role="alert">
                      {content.messages?.success}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <FormErrorBoundary>
      <BaseEmailCaptureForm
        ref={ref}
        theme={theme}
        content={content}
        {...props}
      >
        {renderForm}
      </BaseEmailCaptureForm>
    </FormErrorBoundary>
  )
})

HeroEmailCapture.displayName = 'HeroEmailCapture' 